import { MultiTechAreaGroup } from 'editor/src/store/app/types';
import { Spread } from 'editor/src/store/design/types';

import { DisplayPage } from 'editor/src/component/EditorArea/utils/displayPageUtils';

import { isSameTechnology } from './PageSelector/SpreadThumbnail/techInfo';

interface FilterOptions {
  multiTechAreaGroups?: MultiTechAreaGroup[];
  selectedTechByArea?: Record<string, string>;
  isEmbroideryMode: boolean;
}

const isEmbroiderySpreadName = (name?: string) => {
  const lc = name?.toLowerCase() ?? '';
  return lc.endsWith('-embroidery') || lc.endsWith('-emb');
};

// GAPI-30848: tech-aware shaping of the bottom-strip display pages.
// Three branches:
//   - Embroidery mode (top-level GarmentTechnology = Embroidery): keep only
//     embroidery-suffixed spreads.
//   - Single-tech printing (no multi-tech signal): drop stray -embroidery
//     spreads from mixed templates (Bella DTG ships with
//     chest-center-embroidery) — but only when at least one non-embroidery
//     spread remains. Otherwise we'd empty the strip for pure-embroidery
//     single-tech variants (e.g. Gildan 5000 embroidery, all 5 spreads end
//     in -embroidery).
//   - Multi-tech (per-area picker active): re-order by canonical area,
//     picking the active tech's spread for each group.
export function filterDisplayPagesByTechnology(
  displayPages: DisplayPage[],
  spreads: Spread[],
  { multiTechAreaGroups, selectedTechByArea, isEmbroideryMode }: FilterOptions,
): DisplayPage[] {
  const isMultiTech = !!multiTechAreaGroups?.length && !!selectedTechByArea;

  if (isEmbroideryMode) {
    const embroideryFiltered = displayPages.filter((dp) => isEmbroiderySpreadName(spreads[dp.spreadIndex]?.name));
    return embroideryFiltered.length > 0 ? embroideryFiltered : displayPages;
  }

  if (!isMultiTech) {
    const hasNonEmbroideryArea = displayPages.some((dp) => !isEmbroiderySpreadName(spreads[dp.spreadIndex]?.name));
    return hasNonEmbroideryArea
      ? displayPages.filter((dp) => !isEmbroiderySpreadName(spreads[dp.spreadIndex]?.name))
      : displayPages;
  }

  const dpByName = new Map<string, DisplayPage>();
  displayPages.forEach((dp) => {
    const name = spreads[dp.spreadIndex]?.name;
    if (name) {
      dpByName.set(name, dp);
    }
  });
  const ordered: DisplayPage[] = [];
  multiTechAreaGroups!.forEach((group) => {
    // Mirror the reducer's repair: never fall back onto an option the picker
    // refuses to select, and compare case-insensitively — the host sends
    // lowercase, the editor's own derivation returns capitalised.
    const firstSelectable = group.options.find((opt) => !opt.disabled) ?? group.options[0];
    const activeTech = selectedTechByArea![group.canonicalName] ?? firstSelectable?.technology;
    const match = group.options.find((opt) => isSameTechnology(opt.technology, activeTech)) ?? firstSelectable;
    const dp = match?.spreadName ? dpByName.get(match.spreadName) : undefined;
    if (dp) {
      ordered.push(dp);
    }
  });
  return ordered.length > 0 ? ordered : displayPages;
}

/** Null when the canvas is already on a visible tile. Otherwise the same area's visible spread. */
export function spreadIndexForVisibleTechnology(
  currentSpreadIndex: number,
  displayPages: DisplayPage[],
  spreads: Array<{ name?: string }>,
  groups?: MultiTechAreaGroup[],
): number | null {
  if (!displayPages.length || displayPages.some((page) => page.spreadIndex === currentSpreadIndex)) {
    return null;
  }
  const name = spreads[currentSpreadIndex]?.name;
  const group = name ? groups?.find((item) => item.options.some((option) => option.spreadName === name)) : undefined;
  const sameArea = group
    ? displayPages.find((page) => {
        const pageName = spreads[page.spreadIndex]?.name;
        return !!pageName && group.options.some((option) => option.spreadName === pageName);
      })
    : undefined;
  const target = sameArea ?? displayPages[0];
  return target.spreadIndex === currentSpreadIndex ? null : target.spreadIndex;
}
